import { MessageCircle } from "lucide-react";
import MarketingFooter from "../components/MarketingFooter";
import MarketingPageShell from "../components/MarketingPageShell";
import usePageMeta from "../hooks/usePageMeta";

const TOPICS = [
  "Merchant onboarding & verification",
  "Choosing the right cover tier",
  "Weekly premium payments via M-Pesa",
  "Submitting a claim & checking status",
  "Trust Badge issuance & embed snippets",
  "Renewals, pauses, cancellations",
];

export default function WhatsAppSupport() {
  usePageMeta({
    title: "WhatsApp support — Fortiqa",
    description: "Get help from the Fortiqa team on WhatsApp — onboarding, claims, payments, and Trust Badge support.",
  });
  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#1b1d1c]" data-testid="whatsapp-support-page">
      <MarketingPageShell
        eyebrow="WhatsApp support"
        title="Get help on WhatsApp — fastest for everything."
        intro="Our support team is on WhatsApp 24/7 for claims, and during business hours for everything else. No phone trees, no ticket queues — just a real person."
        testid="whatsapp-support"
      >
        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-12">
          <div className="rounded-3xl bg-white border border-[#114232]/10 p-7 sm:p-10 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="font-heading text-xl sm:text-2xl font-bold text-[#114232] mb-3">
                Message us now
              </div>
              <p className="text-sm sm:text-base text-[#5c6360] leading-relaxed mb-6">
                Tap below to open WhatsApp with a pre-filled hello. We typically respond
                within minutes — and within seconds if you're starting a claim.
              </p>
              <a
                href="https://wa.me/254700000000?text=Hi%20Fortiqa%2C%20I%20need%20help"
                target="_blank"
                rel="noreferrer"
                data-testid="whatsapp-support-cta"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#25D366] hover:bg-[#1ebd5b] text-white font-semibold transition-all shadow-md hover:shadow-lg"
              >
                <MessageCircle className="w-4 h-4" />
                Open WhatsApp · +254 700 000 000
              </a>
              <div className="text-xs text-[#5c6360] mt-3 font-mono">
                Claims: 24/7 · Other support: Mon–Fri 08:00–18:00 EAT
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl bg-[#f4f2eb] border border-[#114232]/8 p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#c15337] font-bold mb-3">
                  We help with
                </div>
                <ul className="space-y-2">
                  {TOPICS.map((t) => (
                    <li key={t} className="flex items-start gap-2 text-sm text-[#1b1d1c]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#c15337] mt-2 shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-24 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { t: "Onboarding", d: "Walk through every step from signup to active cover." },
            { t: "Claims help", d: "We guide you through the evidence checklist and submit on your behalf if you're stuck." },
            { t: "Trust Badge", d: "Send you the embed snippet for your website, WhatsApp catalogue, or IG bio." },
          ].map(({ t, d }) => (
            <div key={t} className="rounded-2xl bg-white border border-[#114232]/10 p-6">
              <div className="font-heading text-base font-bold text-[#114232] mb-1.5">{t}</div>
              <p className="text-sm text-[#5c6360] leading-relaxed">{d}</p>
            </div>
          ))}
        </section>
      </MarketingPageShell>
      <MarketingFooter />
    </div>
  );
}
